import { Lock, RotateCcw, CalendarClock, ArrowRight, Loader2 } from 'lucide-preact'
import { BlastStateBadge } from '@/components/webs/email-blasts/BlastStateBadge'
import { dispatchedCount, resolvedCount, BLAST_STATE_DESCRIPTIONS, type EmailBlastViewDto, type EmailBlastState } from '@/types/emailBlast'

// Send-progress bar fill. Not a per-stage mirror of BLAST_STATE_COLORS — the badge already carries the
// stage hue; the bar uses a coarser progress semantic: in-flight reads as one calm sky, Paused as amber
// caution, Complete as green success, Failed as red danger, Cancelled as slate inert.
const BLAST_PROGRESS_FILL: Record<EmailBlastState, string> = {
  Canary: 'bg-sky-500',
  Measuring: 'bg-sky-500',
  Releasing: 'bg-sky-500',
  Paused: 'bg-amber-500',
  Complete: 'bg-primary-500',
  Failed: 'bg-red-500',
  Cancelled: 'bg-slate-400 dark:bg-slate-500',
}

interface ActivationLockBannerProps {
  blast: EmailBlastViewDto | null
  /** Deep link to this blast's Broadcasts detail, or null when there's no blast (ORB-393). */
  blastHref: string | null
  blastTerminal: boolean
  canStartOver: boolean
  isDisarmed: boolean
  onStartOver?: () => void
  onReschedule?: () => void
  startingOver?: boolean
  rescheduling?: boolean
}

/**
 * Banner shown above the config rail when the activation isn't freely editable (ORB-378): either a blast
 * is staged/in-flight/sent (locked — offer "Cancel & start over" while pre-send) or the activation was
 * started over and is disarmed (offer "Reschedule"). Extracted from EmailActivationPanel (ORB-393).
 */
export function ActivationLockBanner({
  blast,
  blastHref,
  blastTerminal,
  canStartOver,
  isDisarmed,
  onStartOver,
  onReschedule,
  startingOver,
  rescheduling,
}: ActivationLockBannerProps) {
  if (isDisarmed) {
    return (
      <section className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          <span>Not scheduled to send</span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          This activation was reset and is no longer scheduled. Make any edits, then reschedule it to send at its time.
        </p>
        {onReschedule && (
          <button
            type="button"
            onClick={onReschedule}
            disabled={rescheduling}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 hover:bg-primary-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {rescheduling ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />}
            {rescheduling ? 'Rescheduling…' : 'Reschedule'}
          </button>
        )}
      </section>
    )
  }

  if (!blast) return null

  const total = blast.TotalAudience
  const dispatched = dispatchedCount(blast.Stats)
  // The bar counts every row the blast has SETTLED, not just the dispatched ones: skipped rows sit
  // inside TotalAudience and never dispatch, so a completed send with suppressions would otherwise
  // stall the bar short of 100% forever. The label below still speaks about what went out.
  const resolved = resolvedCount(blast.Stats)
  const pct = total > 0 ? Math.min(Math.round((resolved / total) * 100), 100) : 0
  // Gated on the same metric the bar draws. Keying this off `dispatched` while the bar measured
  // `resolved` hid the bar entirely on a blast whose rows were all suppressed — which has settled, and
  // is exactly the state someone opening a locked activation needs to see.
  const showProgress = total > 0 && resolved > 0
  // When rows have settled but the share rounds to 0%, show "<1%" and a thin visible sliver so the
  // bar and label never read 0% / empty while the count says rows are done.
  const pctLabel = resolved > 0 && pct === 0 ? '<1%' : `${pct}%`
  const barWidth = Math.max(pct, 1)

  // canStartOver means a staged send that hasn't dispatched a single row yet (still reversible). Otherwise
  // the send is mid-flight or finished, and the only remaining affordance is to go watch it.
  const lockReason = canStartOver
    ? 'Its send is staged, so the activation is locked.'
    : blastTerminal
      ? 'This activation can no longer be edited.'
      : 'Its send is in progress, so the activation is locked.'
  const detail = canStartOver
    ? `All ${total.toLocaleString()} recipient${total === 1 ? '' : 's'} are queued to send.`
    : BLAST_STATE_DESCRIPTIONS[blast.State]

  return (
    <section className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <Lock className="h-4 w-4 text-slate-500 dark:text-slate-400" aria-hidden="true" />
        <span className="text-sm font-medium text-slate-800 dark:text-slate-100">This activation is locked</span>
        <BlastStateBadge state={blast.State} className="ml-auto" />
      </div>

      <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        {lockReason} <span className="text-slate-500 dark:text-slate-400">{detail}</span>
      </p>

      {showProgress && (
        <div>
          <div className="flex items-baseline justify-between text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
            <span>{dispatched.toLocaleString()} of {total.toLocaleString()} sent</span>
            <span>{pctLabel}</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className={`h-full rounded-full ${BLAST_PROGRESS_FILL[blast.State]}`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {canStartOver && onStartOver && (
          <button
            type="button"
            onClick={onStartOver}
            disabled={startingOver}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {startingOver ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />}
            {startingOver ? 'Starting over…' : 'Cancel & start over'}
          </button>
        )}
        {blastHref && (
          <a
            href={blastHref}
            className="inline-flex items-center gap-1 rounded text-sm font-medium text-primary-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-400"
          >
            View send status
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </div>
    </section>
  )
}
